import { Fragment, useEffect, useState } from "react";
import { ChevronDown, Inbox } from "lucide-react";
import usePagination from "../../hooks/usePagination";
import ListPagination from "./ListPagination";
import {
  getAlignClass,
  getHeaderIcon,
  renderCellContent,
  resolveColumnMeta,
  TableActionCell,
} from "./tableCellContent";

const VISIBLE_DATA_COLUMNS = 4;

function isActionColumn(col) {
  return col?.key === "actions" || typeof col?.actions === "function";
}

function partitionColumns(columns, maxVisible = VISIBLE_DATA_COLUMNS) {
  const actionColumn = columns.find(isActionColumn) || null;
  const detailOnly = columns.filter((col) => !isActionColumn(col) && col.detailOnly);
  const dataColumns = columns.filter((col) => !isActionColumn(col) && !col.detailOnly);
  const limit = Math.max(1, maxVisible);
  if (dataColumns.length <= limit) {
    return { visible: dataColumns, hidden: detailOnly, actionColumn };
  }

  const chosen = new Set();
  const take = (col) => {
    if (!col || chosen.size >= limit) return;
    chosen.add(col.key);
  };

  take(dataColumns[0]);
  take(
    dataColumns.find(
      (col) => col.type === "name" || /name|customer|title|debtor/i.test(String(col.key))
    )
  );
  take(dataColumns.find((col) => col.key === "status" || col.type === "badge"));
  dataColumns.forEach(take);

  return {
    visible: dataColumns.filter((col) => chosen.has(col.key)),
    hidden: [
      ...dataColumns.filter((col) => !chosen.has(col.key)),
      ...detailOnly,
    ],
    actionColumn,
  };
}

function columnValue(col, row) {
  const { type } = resolveColumnMeta(col);
  if (col.actions) return col.actions(row);
  if (col.render) return col.render(row);
  return renderCellContent(row[col.key], type, row);
}

export default function DataTable({
  columns,
  data,
  pageSize = 10,
  emptyMessage = "No records found.",
  onRowClick,
  compact = false,
  selectable = false,
  selectedIds = [],
  getRowId = (row) => row.id ?? row.case_id,
  onToggleRow,
  onTogglePage,
  paginate = true,
  embedded = false,
  maxVisibleColumns = VISIBLE_DATA_COLUMNS,
  resetKey,
  fitContainer = false,
}) {
  const pagination = usePagination(data, pageSize);
  const rows = paginate ? pagination.paginatedItems : data;
  const [openIndex, setOpenIndex] = useState(null);
  useEffect(() => {
    setOpenIndex(null);
  }, [resetKey, pagination.page]);
  const { visible, hidden, actionColumn } = partitionColumns(columns, maxVisibleColumns);
  const showTail = Boolean(actionColumn) || hidden.length > 0;
  const cellPy = compact ? "py-3" : "py-4";
  const pageIds = rows.map((row, index) => String(getRowId(row, index)));
  const selectedSet = new Set((selectedIds || []).map(String));
  const allPageSelected = pageIds.length > 0 && pageIds.every((id) => selectedSet.has(id));
  const somePageSelected = pageIds.some((id) => selectedSet.has(id));
  const colSpan = visible.length + (selectable ? 1 : 0) + (showTail ? 1 : 0);

  if (data.length === 0) {
    return (
      <div className="rounded-xl border border-primary/[0.07] bg-white px-6 py-16 text-center">
        <span className="mx-auto flex h-12 w-12 items-center justify-center rounded-2xl bg-primary/[0.05] text-primary/30">
          <Inbox className="h-6 w-6" />
        </span>
        <p className="mt-3 text-sm font-medium text-primary/50">{emptyMessage}</p>
      </div>
    );
  }

  const tailWidth = hidden.length > 0 ? "w-[5.5rem]" : "w-px whitespace-nowrap";
  const cellPx = hidden.length ? "px-3" : "px-2";
  const table = (
    <div className={fitContainer ? "overflow-hidden" : "overflow-x-auto"}>
      <table
        className={`w-full border-collapse text-left ${fitContainer ? "table-fixed" : "table-auto"}`}
      >
        <thead>
          <tr className="border-b border-primary/[0.08] bg-gradient-to-r from-primary/[0.03] to-accent/[0.04]">
            {selectable ? (
              <th className={`w-10 px-3 ${cellPy}`}>
                <input
                  type="checkbox"
                  checked={allPageSelected}
                  ref={(el) => {
                    if (el) el.indeterminate = somePageSelected && !allPageSelected;
                  }}
                  onChange={() => onTogglePage?.(pageIds, !allPageSelected)}
                  aria-label="Select all rows on this page"
                  className="h-4 w-4 rounded border-primary/30 text-primary focus:ring-accent"
                />
              </th>
            ) : null}
            {visible.map((col) => {
              const { align } = resolveColumnMeta(col);
              const HeaderIcon = getHeaderIcon(col);
              return (
                <th
                  key={col.key}
                  className={`${cellPx} ${cellPy} text-[10px] font-bold uppercase tracking-[0.08em] text-primary/50 ${getAlignClass(align)} ${col.headerClassName ?? ""}`}
                >
                  <span
                    className={`inline-flex max-w-full items-center gap-1.5 truncate ${
                      align === "right" ? "justify-end" : align === "center" ? "justify-center" : ""
                    }`}
                  >
                    {HeaderIcon && (
                      <HeaderIcon className="h-3.5 w-3.5 shrink-0 text-primary/35" strokeWidth={2.25} />
                    )}
                    <span className="truncate">{col.header}</span>
                  </span>
                </th>
              );
            })}
            {showTail ? (
              <th
                className={`${tailWidth} ${cellPx} ${cellPy} text-right text-[10px] font-bold uppercase tracking-[0.08em] text-primary/50`}
              >
                {hidden.length > 0 ? "" : "Actions"}
              </th>
            ) : null}
          </tr>
        </thead>
        <tbody>
          {rows.map((row, index) => {
            const rowId = String(getRowId(row, index) ?? index);
            const isSelected = selectedSet.has(rowId);
            const open = openIndex === index;
            const actionItems = actionColumn?.actions ? actionColumn.actions(row) : null;
            return (
              <Fragment key={`row-${index}`}>
                <tr
                  onClick={onRowClick ? () => onRowClick(row) : undefined}
                  className={`group border-b border-primary/[0.04] transition-all hover:bg-primary/[0.025] ${
                    isSelected ? "bg-accent/[0.08]" : index % 2 === 1 ? "bg-primary/[0.015]" : "bg-white"
                  } ${onRowClick ? "cursor-pointer" : ""}`}
                >
                  {selectable ? (
                    <td className={`w-10 px-3 ${cellPy}`}>
                      <input
                        type="checkbox"
                        checked={isSelected}
                        onClick={(event) => event.stopPropagation()}
                        onChange={() => onToggleRow?.(rowId)}
                        aria-label={`Select ${rowId}`}
                        className="h-4 w-4 rounded border-primary/30 text-primary focus:ring-accent"
                      />
                    </td>
                  ) : null}
                  {visible.map((col) => {
                    const { align } = resolveColumnMeta(col);
                    return (
                      <td
                        key={col.key}
                        className={`${cellPx} ${cellPy} align-middle ${getAlignClass(align)} ${col.className ?? ""}`}
                      >
                        <div
                          className={
                            col.noTruncate
                              ? "min-w-0"
                              : fitContainer
                                ? "min-w-0 truncate"
                                : hidden.length
                                  ? "max-w-[12rem] min-w-0 truncate sm:max-w-none"
                                  : "min-w-0 whitespace-nowrap"
                          }
                          title={col.noTruncate ? undefined : String(row[col.key] ?? "")}
                        >
                          {columnValue(col, row)}
                        </div>
                      </td>
                    );
                  })}
                  {showTail ? (
                    <td className={`${cellPx} ${cellPy} align-middle text-right`}>
                      <div
                        className="flex flex-nowrap items-center justify-end gap-2"
                        onClick={(event) => event.stopPropagation()}
                      >
                        {hidden.length > 0 ? (
                          <button
                            type="button"
                            onClick={() => setOpenIndex(open ? null : index)}
                            className="inline-flex shrink-0 items-center gap-1 rounded-lg border border-primary/10 px-2 py-1 text-[11px] font-semibold text-primary hover:bg-primary/[0.04]"
                            aria-expanded={open}
                          >
                            {open ? "Hide" : "View more"}
                            <ChevronDown className={`h-3.5 w-3.5 transition-transform ${open ? "rotate-180" : ""}`} />
                          </button>
                        ) : null}
                        {actionColumn ? (
                          actionItems ? (
                            <TableActionCell actions={actionItems} />
                          ) : (
                            <TableActionCell>{actionColumn.render ? actionColumn.render(row) : null}</TableActionCell>
                          )
                        ) : null}
                      </div>
                    </td>
                  ) : null}
                </tr>
                {open ? (
                  <tr key={`${rowId}-details`} className="border-b border-primary/[0.06] bg-primary/[0.02]">
                    <td colSpan={colSpan} className="px-4 py-4">
                      <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
                        {hidden.map((col) => (
                          <div
                            key={col.key}
                            className={
                              col.wide || col.key === "push"
                                ? "min-w-0 sm:col-span-2 lg:col-span-3"
                                : "min-w-0"
                            }
                          >
                            <p className="text-[10px] font-bold uppercase tracking-[0.08em] text-primary/40">
                              {col.header}
                            </p>
                            <div className="mt-1 min-w-0 break-words text-sm text-primary">{columnValue(col, row)}</div>
                          </div>
                        ))}
                      </div>
                    </td>
                  </tr>
                ) : null}
              </Fragment>
            );
          })}
        </tbody>
      </table>
    </div>
  );

  if (embedded) return table;

  return (
    <div className="overflow-hidden rounded-xl border border-primary/[0.07] bg-white shadow-[0_1px_3px_rgba(127,29,29,0.06)]">
      {table}
      {paginate ? (
        <ListPagination
          page={pagination.page}
          totalPages={pagination.totalPages}
          totalItems={pagination.totalItems}
          startIndex={pagination.startIndex}
          endIndex={pagination.endIndex}
          goToPage={pagination.goToPage}
          pageSize={pageSize}
          alwaysShow
        />
      ) : null}
    </div>
  );
}
